import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NgControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatFormFieldControl } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule, MatAutocompleteOrigin } from '@angular/material/autocomplete';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, of, Subject } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { normalizeTimeInput } from '@core/components/times/time-input/times-helpers';

@Component({
  selector: 'app-time-input',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatAutocompleteModule,
    MatTooltipModule,
    MatAutocompleteOrigin
  ],
  providers: [
    { provide: MatFormFieldControl, useExisting: TimeInputComponent }
  ],
  templateUrl: './time-input.component.html',
  styleUrl: './time-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimeInputComponent implements ControlValueAccessor, MatFormFieldControl<string | null>, OnInit, OnDestroy, AfterViewInit {
  static nextId: number = 0;

  options: InputSignal<string[]> = input<string[]>([]);

  isOptionDisabled: InputSignal<((opt: string) => boolean) | null> = input<((opt: string) => boolean) | null>(null);

  optionTooltip: InputSignal<((opt: string) => string | null) | null> = input<((opt: string) => string | null) | null>(null);

  readonly placeholderSignal: InputSignal<string> = input<string>('HH:MM', { alias: 'placeholder' });

  readonly requiredSignal: InputSignal<boolean> = input<boolean>(false, { alias: 'required' });

  readonly disabledSignal: InputSignal<boolean> = input<boolean>(false, { alias: 'disabled' });

  private _cvaDisabled: boolean = false;

  @ViewChild('input') inputElement?: ElementRef<HTMLInputElement>;

  public readonly connectedFormField: InputSignal<MatAutocompleteOrigin> = input.required();

  stateChanges: Subject<void> = new Subject<void>();

  @HostBinding() id: string = `app-time-input-${ TimeInputComponent.nextId++ }`;

  controlType: string = 'app-time-input';

  focused: boolean = false;

  touched: boolean = false;

  panelWidth: number = 0;

  private _value: string | null = null;

  filteredOptions$: Observable<string[]> = of([]);

  constructor (
    @Optional() @Self() public ngControl: NgControl,
    @Optional() public parentFormField: MatFormField
  ) {
    this.ngControl.valueAccessor = this;
  }

  // eslint-disable-next-line @tseslint/class-methods-use-this
  onChange: (value: string | null) => void = (): void => {};

  // eslint-disable-next-line @tseslint/class-methods-use-this
  onTouched: () => void = (): void => {};

  ngOnInit (): void {
    this.filteredOptions$ = of(this._value).pipe(
      startWith(this._value ?? ''),
      map(() => this.options())
    );
  }

  ngAfterViewInit (): void {
    const inputWidth: number | undefined = this.inputElement?.nativeElement.offsetWidth;

    this.panelWidth = inputWidth ? Math.max(inputWidth + 17, 100) : 100;
  }

  ngOnDestroy (): void {
    this.stateChanges.complete();
  }

  set value (val: string | null) {
    this._value = val;
    this.onChange(val);
    this.stateChanges.next();
    this.filteredOptions$ = of(val).pipe(
      startWith(val ?? ''),
      map(() => this.options())
    );
  }

  get value (): string | null {
    return this._value;
  }

  get empty (): boolean {
    return !this._value;
  }

  @HostBinding('class.floating')
  get shouldLabelFloat (): boolean {
    return this.focused || !this.empty;
  }

  get errorState (): boolean {
    return Boolean(this.ngControl.invalid && this.touched);
  }

  setDescribedByIds (ids: string[]): void {
    const inputElement: HTMLInputElement | undefined = this.inputElement?.nativeElement;

    if (inputElement)
      inputElement.setAttribute('aria-describedby', ids.join(' '));
  }

  onContainerClick (mouseEvent: MouseEvent): void {
    if ((mouseEvent.target as Element).tagName.toLowerCase() !== 'input')
      this.inputElement?.nativeElement.focus();
  }

  writeValue (value: string | null): void {
    this._value = value;
    this.stateChanges.next();
  }

  registerOnChange (fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched (fn: () => void): void {
    this.onTouched = fn;
  }

  onInput (inputEvent: Event): void {
    if (this.disabled) return;

    const inputEl: HTMLInputElement = inputEvent.target as HTMLInputElement;
    const rawValue: string = inputEl.value.replaceAll(/[^\d]/g, '');
    const digits: string = rawValue.slice(-4);

    let hours: string;
    let minutes: string;

    if (!digits) {
      this.value = '';

      return;
    }

    if (digits.length <= 2) {
      hours = digits;
      minutes = '';
    } else {
      hours = digits.slice(0, -2);
      minutes = digits.slice(-2);
    }

    const formatted: string = `${ hours.padStart(2, '0') }:${ minutes.padEnd(2, '0') }`;

    this.value = formatted;
  }

  onFocus (focusEvent: FocusEvent): void {
    if (this.disabled) return;

    const inputEl: HTMLInputElement = focusEvent.target as HTMLInputElement;

    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }

    if (!this.value)
      this.value = '';


    setTimeout(() => inputEl.select(), 0);
  }

  onKey (keyEvent: KeyboardEvent): void {
    if (this.disabled) return;

    const inputEl: HTMLInputElement = keyEvent.target as HTMLInputElement;
    const pressedKey: string = keyEvent.key;

    if (
      (keyEvent.ctrlKey || keyEvent.metaKey)
      && [ 'a', 'A', 'c', 'C', 'v', 'V', 'x', 'X' ].includes(pressedKey)
    )
      return;


    const navigationKeys: string[] = [ 'Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight' ];

    if (navigationKeys.includes(pressedKey)) return;

    let currentValue: string = String(this.value ?? '');

    const cursorPosition: number = inputEl.selectionStart ?? 0;

    let editPosition: number = cursorPosition;

    const filledValue: string = currentValue.padEnd(5, '-');
    const valueChars: string[] = [ ...filledValue ];

    valueChars[ 2 ] = ':';

    if (pressedKey === 'Backspace' || pressedKey === 'Delete') {
      keyEvent.preventDefault();

      const selStart: number = inputEl.selectionStart ?? 0;
      const selEnd: number = inputEl.selectionEnd ?? selStart;

      if (selEnd > selStart) {
        for (let position: number = selStart; position < selEnd; position++)
          if (position !== 2) valueChars[ position ] = '-';

        editPosition = selStart;
      } else {
        if (pressedKey === 'Backspace')
          editPosition = editPosition === 3 ? 1 : Math.max(0, editPosition - 1);


        if (editPosition === 2) editPosition--;

        if (editPosition >= 0 && editPosition < valueChars.length && valueChars[ editPosition ] !== ':')
          valueChars[ editPosition ] = '-';
      }

      currentValue = valueChars.join('');
      this.value = currentValue;
      inputEl.value = currentValue;
      setTimeout(() => inputEl.setSelectionRange(editPosition, editPosition), 0);

      return;
    }

    if (pressedKey === ':') {
      keyEvent.preventDefault();

      return;
    }

    if (!(/^\d$/).test(pressedKey)) {
      keyEvent.preventDefault();

      return;
    }

    keyEvent.preventDefault();

    if (editPosition === 2) editPosition++;

    if (editPosition >= 5) return;

    valueChars[ editPosition ] = pressedKey;

    const newValue: string = valueChars.join('');

    this.value = newValue;
    inputEl.value = newValue;

    let nextPos: number = editPosition + 1;

    if (nextPos === 2) nextPos++;

    if (nextPos > 5) nextPos = 5;

    setTimeout(() => inputEl.setSelectionRange(nextPos, nextPos), 0);
  }

  onBlur (): void {
    if (!this.focused) return;

    this.focused = false;
    this.touched = true;
    this.onTouched();

    const raw: string | null = this.value;
    const formatted: string | null = normalizeTimeInput(raw);

    this.value = formatted ?? '';
    this.stateChanges.next();
  }

  getTooltip (opt: string): string | null {
    const tooltipFn: ((opt: string) => string | null) | null = this.optionTooltip();

    if (!tooltipFn) return null;

    return tooltipFn(opt);
  }

  setDisabledState (isDisabled: boolean): void {
    this._cvaDisabled = isDisabled;
    this.stateChanges.next();
  }

  get disabled (): boolean {
    return this.disabledSignal() || this._cvaDisabled || Boolean(this.ngControl.disabled);
  }

  isOptionDisabledFor (optionValue: string): boolean {
    return this.isOptionDisabled()?.(optionValue) ?? false;
  }

  onOptionSelected (value: string): void {
    if (this.disabled) return;

    const formatted: string = normalizeTimeInput(value) ?? value;

    this.value = formatted;

    if (this.inputElement)
      this.inputElement.nativeElement.value = formatted;
  }

  get placeholder (): string {
    return this.placeholderSignal();
  }

  get required (): boolean {
    return this.requiredSignal();
  }
}
